/**
 * Where a work location can be, and which timezone and state it must then use.
 *
 * WHY THIS IS STRICT RATHER THAN FREE TEXT. A branch's timezone decides "today" for every attendance
 * punch, shift boundary, and holiday there (CLAUDE.md invariant 3), and its state decides statutory
 * deductions: professional tax and labour welfare fund in India, state income tax in the US. A
 * free-text "Asia/Kolkata (GMT+05:30)" is not an IANA zone any date library accepts, and "Maharastra"
 * is not a state any tax table has. So the country comes from `COUNTRIES` (the jurisdictions this
 * product has, or plans, a payroll pack for), the timezone must be one of that country's zones, and
 * where a state list matters for compliance the code must come from it.
 *
 * Adding a market means adding its country to `COUNTRIES` and its zones here — the compiler then
 * refuses to build until both agree, because `COUNTRY_TIMEZONES` is keyed by `Country`.
 *
 * Pure data and pure functions, so the API validates and the web and mobile forms populate their
 * dropdowns from the same source.
 */

import { type IN_STATES, type Country, type Currency } from '../enums.js';

/**
 * IANA zones per country, the most populous or most common business zone first — the first entry is
 * the default a form preselects.
 */
export const COUNTRY_TIMEZONES: Readonly<Record<Country, readonly [string, ...string[]]>> = {
  IN: ['Asia/Kolkata'],
  US: [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
  ],
  GB: ['Europe/London'],
  AE: ['Asia/Dubai'],
  SG: ['Asia/Singapore'],
  CA: [
    'America/Toronto',
    'America/Vancouver',
    'America/Edmonton',
    'America/Winnipeg',
    'America/Regina',
    'America/Halifax',
    'America/St_Johns',
  ],
  AU: [
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Perth',
    'Australia/Darwin',
    'Australia/Hobart',
  ],
  DE: ['Europe/Berlin'],
  NL: ['Europe/Amsterdam'],
};

/** ISO 3166-2:US subdivision codes — the 50 states and DC, each with its own payroll tax rules. */
export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;

/** ISO 3166-2:AE — the seven emirates. Labour registration and free-zone rules differ by emirate. */
export const AE_EMIRATES = ['AZ', 'AJ', 'DU', 'FU', 'RK', 'SH', 'UQ'] as const;

/**
 * Countries where the state is REQUIRED and must come from a fixed list, because statutory rules
 * are keyed by it. Every other country takes an optional free-text region.
 */
export const COUNTRY_SUBDIVISIONS: Readonly<
  Partial<Record<Country, Readonly<Record<string, string>>>>
> = {
  IN: {
    AN: 'Andaman and Nicobar Islands',
    AP: 'Andhra Pradesh',
    AR: 'Arunachal Pradesh',
    AS: 'Assam',
    BR: 'Bihar',
    CH: 'Chandigarh',
    CT: 'Chhattisgarh',
    DH: 'Dadra and Nagar Haveli and Daman and Diu',
    DL: 'Delhi',
    GA: 'Goa',
    GJ: 'Gujarat',
    HR: 'Haryana',
    HP: 'Himachal Pradesh',
    JK: 'Jammu and Kashmir',
    JH: 'Jharkhand',
    KA: 'Karnataka',
    KL: 'Kerala',
    LA: 'Ladakh',
    LD: 'Lakshadweep',
    MP: 'Madhya Pradesh',
    MH: 'Maharashtra',
    MN: 'Manipur',
    ML: 'Meghalaya',
    MZ: 'Mizoram',
    NL: 'Nagaland',
    OR: 'Odisha',
    PY: 'Puducherry',
    PB: 'Punjab',
    RJ: 'Rajasthan',
    SK: 'Sikkim',
    TN: 'Tamil Nadu',
    TS: 'Telangana',
    TR: 'Tripura',
    UP: 'Uttar Pradesh',
    UT: 'Uttarakhand',
    WB: 'West Bengal',
  } satisfies Record<(typeof IN_STATES)[number], string>,
  US: {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
  } satisfies Record<(typeof US_STATES)[number], string>,
  AE: {
    AZ: 'Abu Dhabi',
    AJ: 'Ajman',
    DU: 'Dubai',
    FU: 'Fujairah',
    RK: 'Ras Al Khaimah',
    SH: 'Sharjah',
    UQ: 'Umm Al Quwain',
  } satisfies Record<(typeof AE_EMIRATES)[number], string>,
};

export function defaultTimezoneFor(country: Country): string {
  return COUNTRY_TIMEZONES[country][0];
}

export type JurisdictionProblem =
  { field: 'timezone'; message: string } | { field: 'stateCode'; message: string };

/**
 * Check that a timezone and state are valid for a country. Returns every problem rather than the
 * first, so a form can mark both fields at once.
 */
export function jurisdictionProblems(input: {
  country: Country;
  timezone: string;
  stateCode: string | null | undefined;
}): JurisdictionProblem[] {
  const problems: JurisdictionProblem[] = [];

  if (!COUNTRY_TIMEZONES[input.country].includes(input.timezone)) {
    problems.push({
      field: 'timezone',
      message: `${input.timezone} is not a supported timezone for ${input.country}`,
    });
  }

  const subdivisions = COUNTRY_SUBDIVISIONS[input.country];
  if (subdivisions) {
    if (!input.stateCode) {
      problems.push({ field: 'stateCode', message: `A state is required for ${input.country}` });
    } else if (!Object.hasOwn(subdivisions, input.stateCode)) {
      problems.push({
        field: 'stateCode',
        message: `${input.stateCode} is not a state of ${input.country}`,
      });
    }
  }

  return problems;
}

/**
 * What a new legal entity in each country usually uses, so a form can preselect it and the API can
 * default it. Fiscal years: April in India and (for tax) the UK, July in Australia, January
 * elsewhere. These are defaults, not rules — an entity may still choose differently.
 */
export const COUNTRY_ENTITY_DEFAULTS: Readonly<
  Record<Country, { currency: Currency; fiscalYearStartMonth: number }>
> = {
  IN: { currency: 'INR', fiscalYearStartMonth: 4 },
  US: { currency: 'USD', fiscalYearStartMonth: 1 },
  GB: { currency: 'GBP', fiscalYearStartMonth: 4 },
  AE: { currency: 'AED', fiscalYearStartMonth: 1 },
  SG: { currency: 'SGD', fiscalYearStartMonth: 1 },
  CA: { currency: 'CAD', fiscalYearStartMonth: 1 },
  AU: { currency: 'AUD', fiscalYearStartMonth: 7 },
  DE: { currency: 'EUR', fiscalYearStartMonth: 1 },
  NL: { currency: 'EUR', fiscalYearStartMonth: 1 },
};
